import { Input } from "@faire/web/slate-deprecated/Input";
import { IErrorProps } from "@faire/web/slate-deprecated/Input/error";
import * as React from "react";

import { DropdownChevron } from "./DropdownChevron";
import { IOptionMixin } from "./types";

interface DropdownInputProps<T extends string> extends IErrorProps {
  id: string;
  dataTestId?: string;
  disabled?: boolean;
  label?: string;
  helper?: string;
  placeholder?: HTMLInputElement["placeholder"];
  disableSearch?: boolean;
  onClick?: () => void;
  onKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  targetRef: React.MutableRefObject<HTMLInputElement | null>;
  borderless?: boolean;
  autoComplete?: string;
  ariaLabel?: string;
  dataHjAllow?: boolean;
  maxLength?: number;
  value?: T;
  onClear?: () => void;
  options: IOptionMixin<T>[];
  searchValue?: string;
  onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
  inputBoxRef?: React.MutableRefObject<HTMLDivElement | null>;
}

export const DropdownInput = <T extends string>({
  dataTestId,
  disabled,
  disableSearch,
  targetRef,
  ariaLabel,
  dataHjAllow,
  value,
  onClick,
  onClear,
  options,
  searchValue,
  onSearch,
  ...props
}: DropdownInputProps<T>) => {
  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!disableSearch) {
        onSearch(e);
      }
    },
    [disableSearch, onSearch]
  );

  const selectedLabel = React.useMemo(
    () => options.find((o) => o.value === value)?.label,
    [options, value]
  );

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      onClick?.();
      e.preventDefault();
      e.stopPropagation();
    },
    [onClick]
  );

  const displayValue = React.useMemo(
    () =>
      disableSearch
        ? (selectedLabel ?? "")
        : (searchValue ?? selectedLabel ?? ""),
    [disableSearch, selectedLabel, searchValue]
  );

  return (
    <Input
      {...props}
      data-test-id={dataTestId}
      disabled={disabled}
      value={displayValue}
      inputMode={disableSearch ? "none" : undefined}
      onChange={handleChange}
      style={{ width: "100%" }}
      suffixNode={
        <DropdownChevron value={value} onClear={onClear} disabled={disabled} />
      }
      ref={targetRef}
      spellCheck={!disableSearch}
      aria-label={ariaLabel}
      data-hj-allow={dataHjAllow}
      hideCharacterCount
      onClick={handleClick}
    />
  );
};
